import { useMemo } from 'react';
import {
  BlockStack,
  Banner,
  Card,
  InlineGrid,
  InlineError,
  Select,
  Text,
  Thumbnail,
} from '@shopify/polaris';
import type { RentalProductConfig } from '@/services/products';

export interface WrapSelection {
  productId: string;
  productTitle: string;
  variantId: string;
  variantTitle?: string;
  sku: string;
  /** Display label, e.g. "Knee Wrap — Left / Medium" */
  label: string;
}

interface WrapPickerProps {
  wraps: RentalProductConfig[];
  /** SKU of the selected wrap variant; '' means none selected */
  selectedSku: string;
  onChange: (selection: WrapSelection | null) => void;
  /** Show a "No wrap" tile (Quick Order allows wrap-less orders) */
  allowNone?: boolean;
  error?: string;
}

function toSelection(
  product: RentalProductConfig,
  variant: RentalProductConfig['variants'][number],
): WrapSelection {
  const hasRealTitle = variant.title && variant.title !== 'Default Title';
  return {
    productId: product.shopifyProductId,
    productTitle: product.title,
    variantId: variant.id,
    variantTitle: hasRealTitle ? variant.title : undefined,
    sku: variant.sku,
    label: hasRealTitle ? `${product.title} — ${variant.title}` : product.title,
  };
}

/**
 * Picture-card wrap selector shared by Quick Order, admin create-for-clinic,
 * and any future order form: pick the wrap family from image tiles, then the
 * Side/Size variant from a dropdown when the family has more than one option.
 * Controlled by the selected variant SKU so callers that prefill from an
 * existing order (copy-order) work without extra state.
 */
export default function WrapPicker({
  wraps,
  selectedSku,
  onChange,
  allowNone = false,
  error,
}: WrapPickerProps) {
  const selected = useMemo(() => {
    if (!selectedSku) return null;
    for (const product of wraps) {
      const variant = product.variants.find((v) => v.sku === selectedSku);
      if (variant) return { product, variant };
    }
    return null;
  }, [wraps, selectedSku]);

  const variants = (selected?.product.variants ?? []).filter((v) => v.sku);
  const isMultiVariant = variants.length > 1;

  const pickProduct = (product: RentalProductConfig) => {
    const firstVariant = product.variants.find((v) => v.sku);
    if (!firstVariant) return;
    onChange(toSelection(product, firstVariant));
  };

  if (wraps.length === 0) {
    return (
      <Banner tone="warning">
        <p>No wraps available — check that wrap products carry the rental-wrap tag.</p>
      </Banner>
    );
  }

  return (
    <BlockStack gap="300">
      <InlineGrid columns={{ xs: 2, sm: 3, md: 4 }} gap="300">
        {allowNone && (
          <div
            onClick={() => onChange(null)}
            style={{
              cursor: 'pointer',
              border: !selected ? '2px solid #008060' : '2px solid transparent',
              borderRadius: '8px',
              padding: '4px',
            }}
          >
            <Card padding="300">
              <BlockStack gap="200" inlineAlign="center">
                <Thumbnail source="" alt="No wrap" size="medium" />
                <Text as="p" variant="bodySm" alignment="center">
                  No wrap
                </Text>
              </BlockStack>
            </Card>
          </div>
        )}
        {wraps.map((product) => {
          const isSelected = selected?.product.shopifyProductId === product.shopifyProductId;
          return (
            <div
              key={product.shopifyProductId}
              onClick={() => pickProduct(product)}
              style={{
                cursor: 'pointer',
                border: isSelected ? '2px solid #008060' : '2px solid transparent',
                borderRadius: '8px',
                padding: '4px',
              }}
            >
              <Card padding="300">
                <BlockStack gap="200" inlineAlign="center">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.title}
                      style={{ width: '100%', maxWidth: '120px', height: 'auto' }}
                    />
                  ) : (
                    <Thumbnail source="" alt={product.title} size="medium" />
                  )}
                  <Text as="p" variant="bodySm" alignment="center">
                    {product.title}
                  </Text>
                </BlockStack>
              </Card>
            </div>
          );
        })}
      </InlineGrid>

      {selected && isMultiVariant && (
        <Select
          label={`${selected.product.title} option`}
          options={variants.map((v) => ({ label: v.title, value: v.sku }))}
          value={selected.variant.sku}
          onChange={(sku) => {
            const variant = variants.find((v) => v.sku === sku);
            if (variant) onChange(toSelection(selected.product, variant));
          }}
          requiredIndicator
        />
      )}

      {selected && (
        <Text as="p" tone="success">
          Selected: {toSelection(selected.product, selected.variant).label}
        </Text>
      )}

      {error && <InlineError message={error} fieldID="wrap-picker" />}
    </BlockStack>
  );
}
